import React from 'react';
import { MdOutlineWarning } from 'react-icons/md';
import { BsExclamationDiamond, BsExclamationTriangle, BsPatchCheck, BsXLg } from 'react-icons/bs';
import * as SC from './styles';

const theme = {
  toast: {
    success: '#09C366',
    successShadow: ' 0px 6px 20px rgba(9, 195, 102, 0.5)',
    error: '#EF4343',
    errorShadow: '0px 6px 20px rgba(239, 67, 67, 0.5)',
    info: '#2196f3',
    warning: '#F07E2A',
    warningShadow: '0px 6px 20px rgba(240, 126, 42, 0.5)',
    dark: '#313131',
  },
};

export const Toast = ({ close, message, type, delay }: IToastProps) => {
  const handleClose = React.useRef(close);
  handleClose.current = close;

  const refProgressBar = React.useRef<HTMLDivElement>(null);
  const delaySerialized = delay / 10;
  const [mouseHover, setMouveHover] = React.useState(false);
  const [timeToClose, setTimeToClose] = React.useState(Math.floor(delaySerialized));
  const progress = 100 - ((delaySerialized - timeToClose) / delaySerialized) * 100;
  const [emitClose, setEmitClose] = React.useState(false);

  const typeConfig: { [key in ToastType]: ITypeConfig } = React.useMemo(
    () => ({
      success: {
        icon: () => <BsPatchCheck />,
        background: theme.toast.success,
        shadow: theme.toast.successShadow,
      },
      warn: {
        icon: () => <BsExclamationTriangle />,
        background: theme.toast.warning,
        shadow: theme.toast.warningShadow,
      },
      info: {
        icon: () => <MdOutlineWarning size={18} />,
        background: theme.toast.info,
      },
      error: {
        icon: () => <BsExclamationDiamond />,
        background: theme.toast.error,
        shadow: theme.toast.errorShadow,
      },
      dark: {
        icon: () => <MdOutlineWarning size={18} />,
        background: theme.toast.dark,
      },
    }),
    [theme],
  );

  const config = typeConfig[type];

  React.useEffect(() => {
    if (mouseHover) return;

    if (emitClose || Math.ceil(timeToClose) <= 0) {
      setEmitClose(true);
      return;
    }

    setTimeout(() => {
      setTimeToClose((prevState) => prevState - 1.5);
    }, 10);
  }, [timeToClose, mouseHover]);

  React.useEffect(() => {
    if (!emitClose) return;
    setTimeout(handleClose.current, 1000);
  }, [emitClose]);

  if (!config) return null;

  const { icon: Icon, background, shadow } = config;

  return (
    <SC.Container
      onMouseOver={() => setMouveHover(true)}
      onMouseLeave={() => setMouveHover(false)}
      background={background}
      shadow={shadow}
      notActive={emitClose}
    >
      <SC.IconContainer>{!!Icon && <Icon />}</SC.IconContainer>

      <SC.MessageContainer>
        {message.split('\n').map((text, index) => (
          <SC.Message key={index}>{text}</SC.Message>
        ))}
      </SC.MessageContainer>

      <SC.CloseBtn type="button" onClick={close}>
        <SC.keyboardInfo>esc</SC.keyboardInfo>
        <BsXLg />
      </SC.CloseBtn>

      <SC.ProgressBar ref={refProgressBar} style={{ width: `${progress}%` }} />
    </SC.Container>
  );
};

export interface IToastProps {
  type: ToastType;
  message: string;
  delay: number;
  close(): void;
}

export type ToastType = 'error' | 'warn' | 'success' | 'info' | 'dark';

interface ITypeConfig {
  icon: () => JSX.Element;
  background: string;
  shadow?: string;
}
